import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { getNow } from "./dateService";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Formats a date into a human readable string.
 * Adjust the default format as needed.
 * Returns empty string for invalid/missing dates.
 */
export function formatDate(
    date: Date | string | number | null | undefined,
    dateFormat: string = "yyyy-MM-dd",
): string {
    if (date === null || date === undefined) return "";
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    return format(d, dateFormat);
}

export function formatDateTime(input: Date | string | number, locale?: string): string {
    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) return "";

    return new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).format(d);
}

/**
 * Debounce a value by `delay` ms.
 */
export function useDebounce<T>(value: T, delay = 300): T {
    const [debounced, setDebounced] = useState<T>(value);

    useEffect(() => {
        const id = window.setTimeout(() => setDebounced(value), delay);
        return () => window.clearTimeout(id);
    }, [value, delay]);

    return debounced;
}

export function getCurrentPeriod(): string {
    const now = getNow();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
}

/** Stable empty array reference — avoids `|| []` creating new refs on every render. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const EMPTY_ARRAY: any[] = [];

/** Stable no-op function — avoids `useCallback(() => {}, [])` allocations. */
export const NOOP: () => void = () => {};
